import React, { useState, useRef, useEffect } from 'react';
import { edgeDeviceService } from '@/services/edgeDeviceService';
import {
  HardDrive, MemoryStick, Clock, Usb, ScrollText, Activity,
  Network, Wifi, FolderOpen, FileText, Send, X, Loader2
} from 'lucide-react';

interface RemoteCommandsPanelProps {
  deviceId: string;
}

interface CommandDef {
  key: string;
  label: string;
  icon: React.ReactNode;
  hasParams?: boolean;
  paramLabel?: string;
  paramDefault?: string;
}

const COMMANDS: CommandDef[] = [
  { key: 'df', label: 'Disk Usage', icon: <HardDrive className="w-4 h-4" /> },
  { key: 'free', label: 'Memory', icon: <MemoryStick className="w-4 h-4" /> },
  { key: 'uptime', label: 'Uptime', icon: <Clock className="w-4 h-4" /> },
  { key: 'lsusb', label: 'USB Devices', icon: <Usb className="w-4 h-4" /> },
  { key: 'systemctl_status', label: 'Service Status', icon: <Activity className="w-4 h-4" /> },
  { key: 'top_snapshot', label: 'Processes', icon: <Activity className="w-4 h-4" /> },
  { key: 'ip_addr', label: 'Network Interfaces', icon: <Network className="w-4 h-4" /> },
  { key: 'ifconfig', label: 'ifconfig', icon: <Network className="w-4 h-4" /> },
  { key: 'iwconfig', label: 'Wireless', icon: <Wifi className="w-4 h-4" /> },
  { key: 'cat_version', label: 'Version', icon: <FileText className="w-4 h-4" /> },
  { key: 'ls_versions', label: 'Versions Dir', icon: <FolderOpen className="w-4 h-4" /> },
  { key: 'dmesg_tail', label: 'Kernel Msgs', icon: <ScrollText className="w-4 h-4" /> },
  { key: 'journalctl_gateway', label: 'Gateway Logs', icon: <ScrollText className="w-4 h-4" />, hasParams: true, paramLabel: 'Lines', paramDefault: '50' },
  { key: 'journalctl_updater', label: 'Updater Logs', icon: <ScrollText className="w-4 h-4" />, hasParams: true, paramLabel: 'Lines', paramDefault: '50' },
  { key: 'journalctl_switch_env', label: 'Env Switch Logs', icon: <ScrollText className="w-4 h-4" />, hasParams: true, paramLabel: 'Lines', paramDefault: '50' },
  { key: 'ping', label: 'Ping', icon: <Send className="w-4 h-4" />, hasParams: true, paramLabel: 'Target', paramDefault: '8.8.8.8' },
];

export const RemoteCommandsPanel: React.FC<RemoteCommandsPanelProps> = ({ deviceId }) => {
  const [output, setOutput] = useState<string>('');
  const [running, setRunning] = useState(false);
  const [activeCommand, setActiveCommand] = useState<string | null>(null);
  const [exitCode, setExitCode] = useState<number | null>(null);
  const [paramValues, setParamValues] = useState<Record<string, string>>({});
  const outputRef = useRef<HTMLPreElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Auto-scroll output
  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [output]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const runCommand = async (cmd: CommandDef) => {
    if (running) return;

    setRunning(true);
    setActiveCommand(cmd.key);
    setOutput('');
    setExitCode(null);

    try {
      // Build params
      const params: Record<string, string> = {};
      if (cmd.hasParams) {
        if (cmd.key.startsWith('journalctl')) {
          params.lines = paramValues[cmd.key] || cmd.paramDefault || '50';
        } else if (cmd.key === 'ping') {
          params.target = paramValues[cmd.key] || cmd.paramDefault || '8.8.8.8';
        }
      }

      const result = await edgeDeviceService.sendCliCommand(deviceId, cmd.key, params);
      const commandId = result.commandId;

      // Poll for results
      pollRef.current = setInterval(async () => {
        try {
          const cmdResult = await edgeDeviceService.getCliCommandResult(deviceId, commandId);
          setOutput(cmdResult.output || '');

          if (cmdResult.status === 'complete') {
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
            setExitCode(cmdResult.exitCode ?? 0);
            setRunning(false);
            setActiveCommand(null);
          }
        } catch {
          // Keep polling
        }
      }, 1500);

    } catch (err) {
      setOutput(`Error: ${err instanceof Error ? err.message : 'Command failed'}`);
      setRunning(false);
      setActiveCommand(null);
    }
  };

  const clearOutput = () => {
    setOutput('');
    setExitCode(null);
  };

  return (
    <div className="space-y-4">
      {/* Command grid */}
      <div>
        <h3 className="text-sm font-medium text-muted-foreground mb-3">Quick Diagnostics</h3>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
          {COMMANDS.map((cmd) => (
            <div key={cmd.key} className="flex flex-col gap-1">
              <button
                onClick={() => runCommand(cmd)}
                disabled={running}
                className={`flex items-center gap-2 px-3 py-2 text-sm rounded border transition-colors ${
                  activeCommand === cmd.key
                    ? 'bg-accent-blue/10 border-accent-blue text-accent-light-blue'
                    : 'bg-card border-border hover:bg-muted text-foreground disabled:opacity-50'
                }`}
              >
                {activeCommand === cmd.key ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  cmd.icon
                )}
                <span className="truncate">{cmd.label}</span>
              </button>
              {cmd.hasParams && (
                <input
                  type="text"
                  value={paramValues[cmd.key] || cmd.paramDefault || ''}
                  onChange={(e) => setParamValues(prev => ({ ...prev, [cmd.key]: e.target.value }))}
                  placeholder={cmd.paramLabel}
                  className="text-xs px-2 py-1 bg-background border border-border rounded text-foreground font-mono"
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Output area */}
      {(output || running) && (
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 bg-muted border-b border-border">
            <span className="text-xs text-muted-foreground font-mono">
              {running ? 'Running...' : exitCode !== null ? `Exit code: ${exitCode}` : 'Output'}
            </span>
            <button
              onClick={clearOutput}
              className="text-muted-foreground hover:text-foreground transition-colors"
              title="Clear"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <pre
            ref={outputRef}
            className="p-3 text-sm font-mono text-foreground bg-background overflow-auto max-h-96 whitespace-pre-wrap"
          >
            {output || (running ? 'Waiting for output...' : '')}
          </pre>
        </div>
      )}
    </div>
  );
};
